import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, Star } from 'lucide-react';
import SectionHeader from './SectionHeader';
import Reveal from './Reveal';
import EditableText from './EditableText';
import { useAuth } from '../context/AuthContext';

function Avatar({ name, src }) {
  const [failed, setFailed] = useState(false);
  const initial = (name || '?').charAt(0).toUpperCase();

  if (!src || failed) {
    return (
      <div
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#3a00e0] to-[#fab03a] text-sm font-semibold text-white"
        aria-hidden="true"
      >
        {initial}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt=""
      className="h-11 w-11 shrink-0 rounded-full object-cover bg-slate-100"
      onError={() => setFailed(true)}
    />
  );
}

const TestimonialsSection = ({
  testimonials,
  eyebrow = 'Témoignages',
  eyebrowKey,
  title = 'Ce que disent les parents',
  titleKey,
  rating = '4.9/5 sur 200+ avis',
  ratingKey,
  className,
  bgClassName = 'bg-slate-50',
}) => {
  const { isAuthenticated, isAdmin, loading: authLoading } = useAuth();
  const showManageCta = !authLoading && isAuthenticated && isAdmin;

  return (
    <section className={`section-y px-4 ${bgClassName} ${className || ''}`}>
      <div className="mx-auto max-w-7xl">
        <SectionHeader
          eyebrow={eyebrow}
          eyebrowKey={eyebrowKey}
          title={title}
          titleKey={titleKey}
          reveal
        />
        <div className="mb-2 flex items-center justify-center gap-1">
          {[...Array(5)].map((_, i) => (
            <Star
              key={i}
              className="h-5 w-5 fill-[#fab03a] text-[#fab03a]"
              aria-hidden="true"
            />
          ))}
        </div>
        {ratingKey ? (
          <EditableText
            editKey={ratingKey}
            defaultValue={rating}
            as="p"
            className="mb-10 text-center text-sm text-slate-500"
          />
        ) : (
          <p className="mb-10 text-center text-sm text-slate-500">{rating}</p>
        )}

        {showManageCta && (
          <div className="mb-6 flex justify-center sm:justify-end">
            <Link
              to="/admin/testimonials"
              className="inline-flex items-center gap-2 rounded-lg border border-[rgba(58,0,224,0.18)] bg-white/80 px-3 py-2 text-sm text-[#3a00e0] transition-colors hover:bg-white"
            >
              <MessageSquare className="h-4 w-4 shrink-0" aria-hidden="true" />
              Gérer les avis
            </Link>
          </div>
        )}

        <div className="grid gap-8 md:grid-cols-3">
          {testimonials.map((testimonial, i) => (
            <Reveal key={testimonial.id} animation="fade-up" delay={i * 55}>
              <blockquote className="h-full">
                <div className="mb-4 flex items-center gap-3">
                  <Avatar name={testimonial.name} src={testimonial.avatar} />
                  <div>
                    <p className="font-medium text-slate-900">{testimonial.name}</p>
                    <p className="text-xs text-slate-500">{testimonial.role}</p>
                  </div>
                </div>
                <div
                  className="mb-3 flex gap-0.5"
                  aria-label={`Note : ${testimonial.rating || 5} sur 5`}
                >
                  {[...Array(testimonial.rating || 5)].map((_, j) => (
                    <Star
                      key={j}
                      className="h-3.5 w-3.5 fill-[#fab03a] text-[#fab03a]"
                      aria-hidden="true"
                    />
                  ))}
                </div>
                <p className="text-sm leading-relaxed text-slate-600">
                  {testimonial.content}
                </p>
              </blockquote>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
